import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { CircularProgress } from "@mui/material";

import * as PlaySettingsActions from "orion-components/GlobalData/WowzaWebRTC/PlaySettings/actionTypes";
import * as WebRTCPlayActions from "orion-components/GlobalData/WowzaWebRTC/WebRTCPlay/actionTypes";
import {
	getPlaySettingByStream,
	getPlaySettingsByStreamAndValue,
	getWebrtcSettingByStreamAndValue
} from "orion-components/GlobalData/Selectors";

import startPlay from "./utils/startPlay";
import stopPlay from "./utils/stopPlay";
import { useState } from "react";
import StreamFailed from "./utils/StreamFailed";

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const loader = () => {
	return (
		<div
			style={{
				marginTop: "19.5%",
				height: "100%",
				width: "100%",
				padding: "2.5%",
				textAlign: "center",
				position: "absolute"
			}}
		>
			<CircularProgress data-testid="orion-components-cbcomponents-video-web-rtcplayer-wowza-player-circular-progress" />
		</div>
	);
};

const Player = ({
	getWowzaStream,
	onStreamFailure,
	errorMessage,
	setPlaySettings,
	wowzaStreamName,
	wowzaApplicationName,
	wowzaSignalingUrl,
	isNewVideoPlayer
}) => {
	const videoElement = useRef(null);
	const webRtcSettings = useRef({});
	const [isLoading, setIsLoading] = useState(true);
	const [socketRetryCount, setSocketRetryCount] = useState(0);
	const [streamError, setStreamError] = useState(false);
	const [videoPlayerStartedContentDisplay, setVideoPlayerStartedContentDisplay] = useState(false);

	const dispatch = useDispatch();

	const playSettings = useSelector(state => getPlaySettingByStream(state, wowzaStreamName));

	const peerConnection = useSelector(state =>
		getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "peerConnection")
	);
	const websocket = useSelector(state => getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "websocket"));
	const connected = useSelector(state => getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "connected"));
	const audioTrack = useSelector(state => getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "audioTrack"));
	const videoTrack = useSelector(state => getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "videoTrack"));
	const isStreamFailed = useSelector(state =>
		getWebrtcSettingByStreamAndValue(state, wowzaStreamName, "isStreamFailed")
	);

	webRtcSettings.current.peerConnection = peerConnection;
	webRtcSettings.current.websocket = websocket;
	webRtcSettings.current.connected = connected;

	const playStart = useSelector(state => getPlaySettingsByStreamAndValue(state, wowzaStreamName, "playStart"));
	const playStarting = useSelector(state => getPlaySettingsByStreamAndValue(state, wowzaStreamName, "playStarting"));
	const playStop = useSelector(state => getPlaySettingsByStreamAndValue(state, wowzaStreamName, "playStop"));
	const playStopping = useSelector(state => getPlaySettingsByStreamAndValue(state, wowzaStreamName, "playStopping"));

	// Listen for changes in the play* flags in the playSettings store
	// and stop or stop playback accordingly

	const wowzaResponseData = {
		streamName: wowzaStreamName,
		applicationName: wowzaApplicationName,
		signalingURL: wowzaSignalingUrl
	};

	const store = useStore();

	const startingPlay = () => {
		dispatch({
			type: PlaySettingsActions.SET_PLAY_FLAGS,
			playStart: false,
			playStarting: true,
			streamName: wowzaStreamName
		});
		startPlay(
			playSettings,
			websocket,
			{
				onError: error => {
					// handle error
					if (hasOwn(error, "websocketError") && socketRetryCount < 3) {
						dispatch({ type: PlaySettingsActions.RESET_PLAY_SETTINGS, data: wowzaResponseData });
						dispatch({ type: WebRTCPlayActions.RESET_WEBRTC, data: wowzaResponseData });
						setPlaySettings();
						setSocketRetryCount(socketRetryCount + 1);
					} else {
						setIsLoading(false);
						setStreamError(true);
					}
				},
				onConnectionStateChange: result => {
					const actionData = { ...wowzaResponseData, ...result };
					dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_CONNECTED, data: actionData });
				},
				onSetPeerConnection: result => {
					const actionData = { ...wowzaResponseData, ...result };
					dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_PEERCONNECTION, data: actionData });
				},
				onSetWebsocket: result => {
					const actionData = { ...wowzaResponseData, ...result };
					dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_WEBSOCKET, data: actionData });
				},
				onPeerConnectionOnTrack: event => {
					if (event.track != null && event.track.kind != null) {
						if (event.track.kind === "audio") {
							const actionData = { audioTrack: event.track, ...wowzaResponseData };
							dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_AUDIO_TRACK, data: actionData });
						} else if (event.track.kind === "video") {
							const actionData = { videoTrack: event.track, ...wowzaResponseData };
							dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_VIDEO_TRACK, data: actionData });
						}
					}
				}
			},
			store
		);
	};

	const stoppingPlay = () => {
		stopPlay(webRtcSettings.current.peerConnection, webRtcSettings.current.websocket, {
			onSetPeerConnection: result => {
				const actionData = { ...wowzaResponseData, ...result };
				dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_PEERCONNECTION, data: actionData });
			},
			onSetWebsocket: result => {
				const actionData = { ...wowzaResponseData, ...result };
				dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_WEBSOCKET, data: actionData });
			},
			onPlayStopped: () => {
				const actionData = { connected: false, ...wowzaResponseData };
				dispatch({ type: WebRTCPlayActions.SET_WEBRTC_PLAY_CONNECTED, data: actionData });
			}
		});
	};

	useEffect(() => {
		if (playStart && !playStarting && (!connected || connected === null)) {
			startingPlay();
		}
		if (playStarting && connected) {
			dispatch({
				type: PlaySettingsActions.SET_PLAY_FLAGS,
				playStarting: false,
				streamName: wowzaResponseData.streamName
			});
		}

		if (playStop && !playStopping && connected) {
			dispatch({
				type: PlaySettingsActions.SET_PLAY_FLAGS,
				playStop: false,
				playStopping: true,
				streamName: wowzaResponseData.streamName
			});
			stoppingPlay();
		}
		if (playStopping && !connected) {
			dispatch({
				type: PlaySettingsActions.SET_PLAY_FLAGS,
				playStopping: false,
				streamName: wowzaResponseData.streamName
			});
		}
	}, [playSettings, peerConnection, websocket, connected, wowzaStreamName]);

	useEffect(() => {
		if (connected) {
			const newStream = new MediaStream();
			if (audioTrack != null) newStream.addTrack(audioTrack);

			if (videoTrack != null) newStream.addTrack(videoTrack);

			if (videoElement != null && videoElement.current != null) {
				videoElement.current.srcObject = newStream;
			}
		} else {
			if (videoElement != null && videoElement.current != null) videoElement.current.srcObject = null;
		}
	}, [audioTrack, videoTrack, connected, videoElement]);

	useEffect(() => {
		if (playStart) {
			setIsLoading(!isLoading);
		} else if (!connected && !playStarting && isLoading) {
			setIsLoading(!isLoading);
		}
	}, [playSettings]);

	useEffect(() => {
		if (isStreamFailed || streamError) {
			onStreamFailure?.();
		}
	}, [isStreamFailed, streamError]);

	const playContent = () => {
		if (!connected && playStarting && isLoading) {
			return loader();
		} else if (isStreamFailed || streamError) {
			return <StreamFailed retryStream={getWowzaStream} message={errorMessage} />;
		} else {
			return (
				<>
					{!videoPlayerStartedContentDisplay && loader()}
					<div
						id="WebRTCPlayer-play-video-container"
						style={{
							height: "100%",
							width: "100%"
						}}
					>
						<video
							id="player-video"
							style={{
								height: "100%",
								width: "100%",
								...(!isNewVideoPlayer && { padding: "2.5%" })
							}}
							ref={videoElement}
							onLoadedData={() => {
								setVideoPlayerStartedContentDisplay(true);
							}}
							autoPlay
							playsInline
							muted data-testid="orion-components-cbcomponents-video-web-rtcplayer-wowza-player-video"
						></video>
					</div>
				</>
			);
		}
	};

	return playContent();
};

export default Player;
